import React, { useState, useEffect, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { api } from '../api/client'
import { useToast } from '../context/useToast'
import PageShell from '../components/ui/PageShell'
import Card from '../components/ui/Card'
import SectionTitle from '../components/ui/SectionTitle'
import Badge from '../components/ui/Badge'
import { SecondaryButton, DangerButton } from '../components/ui/Button'
import Skeleton from '../components/ui/Skeleton'
import ProgressBar from '../components/ProgressBar'
import { estimatedSecondsRemaining, formatRemaining } from '../lib/jobEta'

const STEP_LABELS = {
  preflight: 'Preflight',
  decode: 'Decode',
  analyze: 'Analyse',
  plan: 'Plan',
  ai_cleanup: 'AI Cleanup',
  render: 'Render',
  limit_safety: 'Limit/Safety',
  qc: 'Qualitätsprüfung',
  tagging: 'Tagging',
  analyzing: 'Analyse',
  mastering: 'Plan',
  rendering: 'Render',
  finalizing: 'Abschluss',
}

const STATUS_LABELS = {
  pending: 'In Warteschlange',
  running: 'Wird verarbeitet',
  completed: 'Fertig',
  failed: 'Fehlgeschlagen',
  cancelled: 'Abgebrochen',
}

const JOB_TYPE_LABELS = {
  full_pipeline: 'Mastering',
  cover: 'Cover',
  canvas: 'Canvas',
}

export default function JobQueue() {
  const toast = useToast()
  const [jobs, setJobs] = useState([])
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState('all') // all | running | done | failed
  const [workerStatus, setWorkerStatus] = useState(null)

  useEffect(() => {
    const fetchJobs = () => {
      api.get('/jobs')
        .then((r) => setJobs(r.data.jobs || []))
        .catch(() => setJobs([]))
        .finally(() => setLoading(false))
    }
    const fetchWorkerStatus = () => {
      api.get('/worker-status')
        .then((r) => setWorkerStatus(r.data))
        .catch(() => setWorkerStatus({ status: 'error' }))
    }
    fetchJobs()
    fetchWorkerStatus()
    const t = setInterval(fetchJobs, 4000)
    const tw = setInterval(fetchWorkerStatus, 4000)
    return () => { clearInterval(t); clearInterval(tw) }
  }, [])

  const filteredJobs = useMemo(() => {
    if (filter === 'running') return jobs.filter((j) => j.status === 'pending' || j.status === 'running')
    if (filter === 'done') return jobs.filter((j) => j.status === 'completed')
    if (filter === 'failed') return jobs.filter((j) => j.status === 'failed' || j.status === 'cancelled')
    return jobs
  }, [jobs, filter])

  const cancelJob = async (jobId) => {
    if (!window.confirm('Job wirklich abbrechen?')) return
    try {
      await api.patch(`/jobs/${jobId}`, { status: 'cancelled' })
      setJobs((prev) => prev.map((j) => (j.id === jobId ? { ...j, status: 'cancelled', result_message: 'Abgebrochen' } : j)))
      toast('Job abgebrochen', 'success')
    } catch (e) {
      toast(e.message || 'Abbrechen fehlgeschlagen', 'error')
    }
  }

  const deleteJob = async (jobId) => {
    if (!window.confirm('Job endgültig aus der Liste entfernen?')) return
    try {
      await api.delete(`/jobs/${jobId}`)
    } catch (e) {
      // 404 ok
    }
    setJobs((prev) => prev.filter((j) => j.id !== jobId))
    toast('Job entfernt', 'success')
  }

  return (
    <PageShell>
      <div className="space-y-8">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-white">Job-Queue</h1>
          <p className="mt-2 text-white/70">
            Aktuelle und letzte Mastering-Jobs. Der Worker verarbeitet die Queue nacheinander.
          </p>
        </div>

        {workerStatus && (
          <Card>
            <SectionTitle className="mb-3">Worker</SectionTitle>
            <div className="flex flex-wrap items-center gap-3 text-sm">
              {workerStatus.status === 'busy' && (
                <>
                  <span className="h-2.5 w-2.5 rounded-full bg-amber-400 animate-pulse" aria-hidden />
                  <span className="text-amber-300 font-medium">Beschäftigt</span>
                  {workerStatus.track_id != null && (
                    <Link to={`/dashboard/tracks/${workerStatus.track_id}`} className="text-[#00d4aa] hover:text-[#99f6e4] underline underline-offset-2">
                      Track {workerStatus.track_id} öffnen
                    </Link>
                  )}
                </>
              )}
              {workerStatus.status === 'idle' && (
                <>
                  <span className="h-2.5 w-2.5 rounded-full bg-emerald-500" aria-hidden />
                  <span className="text-white/90">Bereit</span>
                </>
              )}
              {(workerStatus.status === 'unknown' || workerStatus.status === 'error') && (
                <>
                  <span className="h-2.5 w-2.5 rounded-full bg-white/40" aria-hidden />
                  <span className="text-white/60">{workerStatus.message || 'Status unbekannt'}</span>
                </>
              )}
            </div>
          </Card>
        )}

        <Card>
          <SectionTitle className="mb-4">Jobs</SectionTitle>
          <div className="mb-4 flex flex-wrap gap-2">
            {[
              { id: 'all', label: 'Alle' },
              { id: 'running', label: 'Läuft' },
              { id: 'done', label: 'Fertig' },
              { id: 'failed', label: 'Fehlgeschlagen' },
            ].map((f) => (
              <button
                key={f.id}
                type="button"
                onClick={() => setFilter(f.id)}
                className={`rounded-lg border px-4 py-2 text-sm font-medium transition ${
                  filter === f.id
                    ? 'border-[#00d4aa]/50 bg-[#00d4aa]/15 text-white'
                    : 'border-white/10 bg-white/5 text-white/70 hover:border-white/20 hover:text-white'
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>

          {loading ? (
            <div className="space-y-3">
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
            </div>
          ) : filteredJobs.length === 0 ? (
            <div className="rounded-xl border border-white/10 bg-white/5 py-12 text-center text-white/60">
              {filter === 'all' ? 'Keine Jobs in der Queue.' : 'Keine Jobs in dieser Kategorie.'}
            </div>
          ) : (
            <ul className="space-y-3">
              {filteredJobs.map((j) => {
                const running = j.status === 'pending' || j.status === 'running'
                const remainingSec = estimatedSecondsRemaining(j)
                const stepLabel = j.step ? STEP_LABELS[j.step] || j.step : null
                const typeLabel = JOB_TYPE_LABELS[j.job_type] || j.job_type
                const isCoverOrCanvas = j.job_type === 'cover' || j.job_type === 'canvas'
                const runningLabel = isCoverOrCanvas
                  ? (j.job_type === 'cover' ? 'Cover wird erstellt' : 'Canvas wird erstellt')
                  : (stepLabel || 'Wird verarbeitet…')
                const statusVariant = j.status === 'completed' ? 'ready' : j.status === 'failed' || j.status === 'cancelled' ? 'failed' : j.status === 'running' ? 'running' : 'pending'
                return (
                  <li key={j.id} className="rounded-xl border border-white/10 bg-white/5 px-4 py-3">
                    <div className="flex flex-wrap items-center gap-3 gap-y-2">
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <Link to={`/dashboard/tracks/${j.track_id}`} className="font-medium text-white hover:text-[#99f6e4] truncate max-w-[200px] sm:max-w-none">
                            {j.track_title ?? `Track ${j.track_id}`}
                          </Link>
                          <Badge variant={statusVariant}>{STATUS_LABELS[j.status] || j.status}</Badge>
                          <span className="text-xs text-white/50">{typeLabel}</span>
                        </div>
                        {running && (
                          <div className="mt-1.5 flex flex-wrap items-center gap-2">
                            <span className="text-sm text-white/70">{runningLabel}</span>
                            {remainingSec != null && <span className="text-sm font-medium text-[#00d4aa]">Noch ca. {formatRemaining(remainingSec)}</span>}
                          </div>
                        )}
                        {running && j.progress != null && (
                          <div className="mt-1.5 max-w-xs">
                            <ProgressBar progress={j.progress} variant="default" showLabel={false} />
                            <span className="text-xs text-white/50">{j.progress}%</span>
                          </div>
                        )}
                        {!running && j.result_message && <p className="mt-1 text-xs text-white/50 truncate max-w-full" title={j.result_message}>{j.result_message}</p>}
                      </div>
                      <div className="flex shrink-0 items-center gap-2">
                        <Link to={`/dashboard/tracks/${j.track_id}`} className="text-sm text-[#00d4aa] hover:text-[#99f6e4]">Öffnen</Link>
                        {running && <SecondaryButton onClick={() => cancelJob(j.id)}>Abbrechen</SecondaryButton>}
                        <DangerButton onClick={() => deleteJob(j.id)}>Löschen</DangerButton>
                      </div>
                    </div>
                  </li>
                )
              })}
            </ul>
          )}
        </Card>
      </div>
    </PageShell>
  )
}
